<template>
  <RevCard class="flex w-full flex-col gap-32 p-32">
    <div class="flex items-start">
      <ProductInfo :counter-offer-price :image :price :tag :title />
      <RevButton
        v-if="hasCounterOffer"
        class="ml-auto"
        data-qa="view-counter-offer-button"
        :loading="isLoadingCounterOffer"
        size="small"
        variant="secondary"
        @click="sendViewCounterOffer"
      >
        {{ i18n(translations.viewCounteroffer) }}
      </RevButton>
    </div>

    <h2 class="heading-2">
      {{ i18n(translations.assessmentHeading) }}
    </h2>

    <div>
      <div
        v-for="(item, index) in formattedAssessment"
        :key="item.name"
        class="flex flex-col"
      >
        <div class="flex items-center gap-12">
          <component :is="item.icon" />
          <p class="text-static-default-low body-2">
            {{ item.label }}
          </p>
          <p class="text-static-default-low body-2-bold grow text-right">
            {{ item.value }}
          </p>
        </div>
        <RevDivider
          v-if="!isLastElement(index, formattedAssessment)"
          class="my-24"
        />
      </div>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { CustomerAssessmentItem } from '@backmarket/http-api/src/api-specs-buyback/customer/getCustomerSaleDetails'
import ProductInfo from '@backmarket/nuxt-layer-buyback/components/ProductInfo/ProductInfo.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { IconBattery } from '@ds/icons/IconBattery'
import { IconEyeOpen } from '@ds/icons/IconEyeOpen'
import { IconHeartDiagnostic } from '@ds/icons/IconHeartDiagnostic'
import { IconSim } from '@ds/icons/IconSim'

import translations from './CustomerAssessment.translations'

const i18n = useI18n()
const emit = defineEmits(['view-counteroffer'])
const props = withDefaults(
  defineProps<{
    title: string
    price?: string
    counterOfferPrice?: string
    tag?: string
    image?: string
    assessment: CustomerAssessmentItem[]
    isLoadingCounterOffer?: boolean
  }>(),
  {
    price: '',
    counterOfferPrice: '',
    tag: '',
    image: '',
    assessment: () => [],
    isLoadingCounterOffer: false,
  },
)

const assessmentIcons = {
  batteryState: IconBattery,
  simState: IconSim,
  carrier: IconSim,
  appearance: IconEyeOpen,
  functionalState: IconHeartDiagnostic,
}

const formattedAssessment = computed(() => {
  return props.assessment.map(({ label, name, value }) => {
    const icon = assessmentIcons[name as keyof typeof assessmentIcons]

    return {
      label,
      name,
      value,
      icon,
    }
  })
})
const hasCounterOffer = computed(() => !isEmpty(props.counterOfferPrice))

function isLastElement(
  index: number,
  array: typeof formattedAssessment.value,
): boolean {
  return index === array.length - 1
}

function sendViewCounterOffer(): void {
  emit('view-counteroffer')
}
</script>
